import { useState, useEffect, useRef } from "react";
import { supabase, SUPABASE_URL, ENLACE_RECUPERACION, ENLACE_EXPIRADO } from './supabase';

// ─── BRAND ───────────────────────────────────────────────────────
import LOGO from "./logo1.png";
const REGISTRO_FN = `${SUPABASE_URL}/functions/v1/registro-cliente`;

const C = {
  blue:   "#1b57ce",
  blueD:  "#1245a8",
  blueL:  "#e8f0fd",
  orange: "#ea592d",
  yellow: "#f6e24d",
  white:  "#ffffff",
  bg:     "#f0f4ff",
  card:   "#ffffff",
  text:   "#111827",
  muted:  "#6b7280",
  border: "#e5e7eb",
  error:  "#ef4444",
  green:  "#16a34a",
};

// ─── STEPPER ─────────────────────────────────────────────────────
function Stepper({ step }) {
  const steps = ["Cuenta", "Personal", "Documento", "Listo"];
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 0, marginBottom: 32 }}>
      {steps.map((label, i) => {
        const active = i === step;
        const done   = i < step;
        return (
          <div key={i} style={{ display: "flex", alignItems: "center", flex: i < steps.length - 1 ? 1 : "none" }}>
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 4 }}>
              <div style={{
                width: 32, height: 32, borderRadius: "50%",
                background: done ? C.green : active ? C.blue : C.border,
                color: done || active ? C.white : C.muted,
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: 13, fontWeight: 800, transition: "all .3s",
                boxShadow: active ? `0 0 0 4px rgba(27,87,206,0.2)` : "none"
              }}>
                {done ? "✓" : i + 1}
              </div>
              <span style={{ fontSize: 10, fontWeight: 700, color: active ? C.blue : done ? C.green : C.muted, whiteSpace: "nowrap" }}>
                {label}
              </span>
            </div>
            {i < steps.length - 1 && (
              <div style={{ flex: 1, height: 2, background: done ? C.green : C.border, margin: "0 6px", marginBottom: 20, transition: "background .3s" }} />
            )}
          </div>
        );
      })}
    </div>
  );
}

// ─── INPUT ────────────────────────────────────────────────────────
function Input({ label, type = "text", placeholder, value, onChange, onKeyDown, error, hint, prefix, required }) {
  const [focused, setFocused] = useState(false);
  return (
    <div style={{ marginBottom: 18 }}>
      <label style={{ display: "block", fontSize: 11, fontWeight: 800, color: C.blue, letterSpacing: 1.5, textTransform: "uppercase", marginBottom: 7 }}>
        {label}{required && <span style={{ color: C.orange, marginLeft: 3 }}>*</span>}
      </label>
      <div style={{ position: "relative", display: "flex", alignItems: "center" }}>
        {prefix && (
          <span style={{ position: "absolute", left: 14, fontSize: 14, fontWeight: 700, color: C.muted }}>
            {prefix}
          </span>
        )}
        <input
          type={type}
          placeholder={placeholder}
          value={value}
          onChange={onChange}
          onKeyDown={onKeyDown}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          style={{
            width: "100%", padding: prefix ? "13px 14px 13px 36px" : "13px 16px",
            borderRadius: 12, border: `2px solid ${error ? C.error : focused ? C.blue : C.border}`,
            background: C.white, fontFamily: "inherit", fontSize: 15, fontWeight: 600, color: C.text,
            outline: "none", transition: "border .2s",
            boxSizing: "border-box",
          }}
        />
      </div>
      {hint && !error && <div style={{ fontSize: 11, color: C.muted, marginTop: 5 }}>{hint}</div>}
      {error && <div style={{ fontSize: 11, color: C.error, marginTop: 5, fontWeight: 700 }}>⚠ {error}</div>}
    </div>
  );
}

// ─── STEP 1: EMAIL + CONTRASEÑA ───────────────────────────────────
function Step1({ data, setData, onNext }) {
  const [errors, setErrors] = useState({});
  const [showPass, setShowPass] = useState(false);
  const [checking, setChecking] = useState(false);

  async function validate() {
    const e = {};
    if (!data.email.includes("@")) e.email = "Ingresa un correo válido";
    if (data.password.length < 8)   e.password = "Mínimo 8 caracteres";
    if (data.password !== data.confirm) e.confirm = "Las contraseñas no coinciden";
    setErrors(e);
    if (Object.keys(e).length > 0) return;
    setChecking(true);
    let existe = false;
    try {
      const r = await fetch(REGISTRO_FN, { method:"POST", headers:{ "Content-Type":"application/json" }, body: JSON.stringify({ accion:"existe", email: data.email.trim() }) });
      const d = await r.json().catch(() => ({}));
      existe = d.existe === true;
    } catch { /* si falla la consulta, se valida de nuevo al registrar */ }
    setChecking(false);
    if (existe) {
      setErrors({ email: "Este correo ya está registrado" });
      return;
    }
    onNext();
  }

  return (
    <div>
      <Input label="Correo electrónico" type="email" placeholder="tu@correo.com"
        value={data.email} onChange={e => setData({ ...data, email: e.target.value })}
        error={errors.email} required />
      <Input label="Contraseña" type={showPass ? "text" : "password"} placeholder="Mínimo 8 caracteres"
        value={data.password} onChange={e => setData({ ...data, password: e.target.value })}
        error={errors.password} required />
      <Input label="Confirmar contraseña" type={showPass ? "text" : "password"} placeholder="Repite tu contraseña"
        value={data.confirm} onChange={e => setData({ ...data, confirm: e.target.value })}
        error={errors.confirm} required />
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 24, marginTop: -8 }}>
        <input type="checkbox" id="showpass" checked={showPass} onChange={e => setShowPass(e.target.checked)} style={{ accentColor: C.blue }} />
        <label htmlFor="showpass" style={{ fontSize: 13, color: C.muted, cursor: "pointer", fontWeight: 600 }}>Mostrar contraseña</label>
      </div>
      <button onClick={validate} disabled={checking} style={{ ...btnStyle(C.blue), opacity: checking ? 0.7 : 1 }}>
        {checking ? "Verificando..." : "Continuar →"}
      </button>
    </div>
  );
}

// ─── STEP 2: DATOS PERSONALES ──────────────────────────────────────
function Step2({ data, setData, onNext, onBack }) {
  const [errors, setErrors] = useState({});

  function validate() {
    const e = {};
    if (!data.nombre.trim())   e.nombre = "Ingresa tu nombre";
    if (!data.apellido.trim()) e.apellido = "Ingresa tu apellido";
    if (!data.telefono.trim() || data.telefono.replace(/\D/g,"").length < 6)
      e.telefono = "Ingresa un número de teléfono válido";
    setErrors(e);
    return Object.keys(e).length === 0;
  }

  return (
    <div>
      <Input label="Nombre(s)" placeholder="ej. Carlos"
        value={data.nombre} onChange={e => setData({ ...data, nombre: e.target.value })}
        error={errors.nombre} required />
      <Input label="Apellido(s)" placeholder="ej. García"
        value={data.apellido} onChange={e => setData({ ...data, apellido: e.target.value })}
        error={errors.apellido} required />
      <div style={{ marginBottom: 18 }}>
        <label style={{ display:"block", fontSize:11, fontWeight:800, color:C.blue, letterSpacing:1.5, textTransform:"uppercase", marginBottom:7 }}>
          Teléfono WhatsApp <span style={{ color: C.orange, marginLeft:3 }}>*</span>
        </label>
        <div style={{ display:"flex", gap:8 }}>
          <select value={data.telefono_pais || "+503"} onChange={e => setData({ ...data, telefono_pais: e.target.value })}
            style={{ padding:"13px 10px", borderRadius:12, border:`2px solid ${errors.telefono?C.error:C.border}`, background:C.white, fontFamily:"inherit", fontSize:14, fontWeight:700, color:C.text, outline:"none", flexShrink:0 }}>
            {[
              ["+503","🇸🇻 +503"],["+1","🇺🇸 +1"],["+52","🇲🇽 +52"],["+502","🇬🇹 +502"],
              ["+504","🇭🇳 +504"],["+505","🇳🇮 +505"],["+506","🇨🇷 +506"],["+507","🇵🇦 +507"],
              ["+57","🇨🇴 +57"],["+58","🇻🇪 +58"],["+51","🇵🇪 +51"],["+56","🇨🇱 +56"],
              ["+54","🇦🇷 +54"],["+55","🇧🇷 +55"],["+34","🇪🇸 +34"],["+44","🇬🇧 +44"],
            ].map(([v,l]) => <option key={v} value={v}>{l}</option>)}
          </select>
          <input type="tel" placeholder="ej. 79001234" value={data.telefono}
            onChange={e => setData({ ...data, telefono: e.target.value })}
            style={{ flex:1, padding:"13px 16px", borderRadius:12, border:`2px solid ${errors.telefono?C.error:C.border}`, background:C.white, fontFamily:"inherit", fontSize:15, fontWeight:600, color:C.text, outline:"none", boxSizing:"border-box" }}/>
        </div>
        <div style={{ fontSize:11, color:C.muted, marginTop:5 }}>Te notificaremos por WhatsApp en cada etapa de tu paquete</div>
        {errors.telefono && <div style={{ fontSize:11, color:C.error, marginTop:5, fontWeight:700 }}>⚠ {errors.telefono}</div>}
      </div>
      <div style={{ background: C.blueL || "#e8f0fd", borderRadius: 12, padding: "12px 16px", marginBottom: 20, fontSize: 13, color: C.blue, fontWeight: 600, lineHeight: 1.6 }}>
        💡 Tu código de cliente se asignará automáticamente al completar el registro.
      </div>
      <div style={{ display: "flex", gap: 12 }}>
        <button onClick={onBack} style={btnStyle(C.border, C.muted, true)}>← Atrás</button>
        <button onClick={() => validate() && onNext()} style={{ ...btnStyle(C.blue), flex: 2 }}>Continuar →</button>
      </div>
    </div>
  );
}

// ─── STEP 3: SUBIR DUI ────────────────────────────────────────────
function Step3({ data, setData, onSubmit, onBack, loading }) {
  const [preview, setPreview]   = useState(null);
  const [dragging, setDragging] = useState(false);
  const [error, setError]       = useState("");
  // useRef para evitar stale closure — siempre tenemos el archivo más reciente
  const duiFileRef = useRef(null);

  function handleFile(file) {
    if (!file) return;
    const allowed = ["image/jpeg","image/png","image/jpg","application/pdf"];
    if (!allowed.includes(file.type)) { setError("Solo se aceptan imágenes JPG, PNG o PDF"); return; }
    if (file.size > 5 * 1024 * 1024)  { setError("El archivo no debe superar 5MB"); return; }
    setError("");
    duiFileRef.current = file;
    setData({ ...data, dui: file });
    if (file.type.startsWith("image/")) {
      const reader = new FileReader();
      reader.onload = e => setPreview(e.target.result);
      reader.readAsDataURL(file);
    } else {
      setPreview("pdf");
    }
  }

  function handleSubmit() {
    if (!data.dui_numero) { setError("Ingresa tu número de DUI"); return; }
    const duiRegex = /^\d{8}-\d$/;
    if (!duiRegex.test(data.dui_numero.trim())) { setError("Formato incorrecto. Debe ser: 00000000-0 (8 números, guión, 1 número)"); return; }
    const fileToUse = duiFileRef.current || data.dui;
    if (!fileToUse) { setError("La foto del DUI es obligatoria"); return; }
    // Pasamos el archivo directamente como parámetro para evitar stale closure
    onSubmit(fileToUse);
  }

  return (
    <div>
      <p style={{ fontSize: 14, color: C.muted, lineHeight: 1.7, marginBottom: 20, fontWeight: 600 }}>
        Necesitamos tu número de DUI y una foto para realizar las declaraciones aduanales.
      </p>
      <div style={{ marginBottom: 20 }}>
        <label style={{ display: "block", fontSize: 11, fontWeight: 800, color: C.blue, letterSpacing: 1.5, textTransform: "uppercase", marginBottom: 7 }}>
          Número de DUI
        </label>
        <input
          type="text"
          placeholder="01234567-8"
          value={data.dui_numero || ""}
          onChange={e => setData({ ...data, dui_numero: e.target.value })}
          style={{ width: "100%", padding: "13px 16px", borderRadius: 12, border: `2px solid ${C.border}`, fontSize: 15, fontWeight: 600, outline: "none", fontFamily: "inherit", boxSizing: "border-box" }}
        />
      </div>
      {!preview ? (
        <div
          onDragOver={e => { e.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={e => { e.preventDefault(); setDragging(false); handleFile(e.dataTransfer.files[0]); }}
          onClick={() => document.getElementById("dui-input").click()}
          style={{
            border: `2px dashed ${dragging ? C.blue : error ? C.error : C.border}`,
            borderRadius: 16, padding: "40px 24px", textAlign: "center",
            cursor: "pointer", transition: "all .2s", marginBottom: 16,
            background: dragging ? "#e8f0fd" : C.white,
          }}
        >
          <div style={{ fontSize: 48, marginBottom: 12 }}>🪪</div>
          <div style={{ fontSize: 15, fontWeight: 800, color: C.text, marginBottom: 6 }}>
            {dragging ? "Suelta aquí" : "Foto del DUI *"}
          </div>
          <div style={{ fontSize: 13, color: C.muted, fontWeight: 600 }}>
            Obligatorio · Arrastra o haz clic · JPG, PNG o PDF · Máx. 5MB
          </div>
          <input id="dui-input" type="file" accept="image/*,.pdf" style={{ display: "none" }}
            onChange={e => handleFile(e.target.files[0])} />
        </div>
      ) : (
        <div style={{ marginBottom: 16, borderRadius: 16, overflow: "hidden", border: `2px solid ${C.green}`, position: "relative" }}>
          {preview === "pdf" ? (
            <div style={{ background: "#fef2f2", padding: "24px", textAlign: "center" }}>
              <div style={{ fontSize: 40, marginBottom: 8 }}>📄</div>
              <div style={{ fontSize: 14, fontWeight: 700, color: C.text }}>{data.dui?.name}</div>
            </div>
          ) : (
            <img src={preview} alt="DUI" style={{ width: "100%", maxHeight: 200, objectFit: "cover", display: "block" }} />
          )}
          <div style={{ background: C.green, padding: "8px 16px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <span style={{ fontSize: 13, fontWeight: 700, color: C.white }}>✓ Foto subida</span>
            <button onClick={() => { setPreview(null); setData({ ...data, dui: null }); duiFileRef.current = null; }}
              style={{ background: "rgba(255,255,255,0.2)", border: "none", borderRadius: 6, padding: "4px 10px", color: C.white, fontSize: 12, fontWeight: 700, cursor: "pointer" }}>
              Cambiar
            </button>
          </div>
        </div>
      )}
      {error && <div style={{ fontSize: 12, color: C.error, fontWeight: 700, marginBottom: 12 }}>⚠ {error}</div>}
      <div style={{ background: "#fffbeb", border: "1px solid #fde68a", borderRadius: 12, padding: "12px 16px", marginBottom: 24, fontSize: 12, color: "#92400e", fontWeight: 600, lineHeight: 1.6 }}>
        🔒 Tu documento está protegido. Lo utilizamos una sola vez para verificar tu identidad y realizar declaraciones aduanales. Una vez verificado, tu DUI es eliminado de nuestros sistemas.
      </div>
      <div style={{ display: "flex", gap: 12 }}>
        <button onClick={onBack} disabled={loading} style={{ ...btnStyle(C.border, C.muted, true), opacity: loading ? 0.5 : 1 }}>← Atrás</button>
        <button onClick={handleSubmit} disabled={loading} style={{ ...btnStyle(C.blue, C.white), flex: 2, opacity: loading ? 0.7 : 1 }}>
          {loading ? "Creando cuenta..." : "Crear cuenta →"}
        </button>
      </div>
    </div>
  );
}

// ─── STEP 4: ÉXITO ────────────────────────────────────────────────
function Step4({ data }) {
  return (
    <div style={{ textAlign: "center" }}>
      <div style={{ width: 80, height: 80, borderRadius: "50%", background: "rgba(22,163,74,0.1)", border: "3px solid #16a34a", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 36, margin: "0 auto 20px" }}>
        ✓
      </div>
      <h2 style={{ fontSize: 24, fontWeight: 900, color: C.text, marginBottom: 8 }}>¡Cuenta creada!</h2>
      <p style={{ fontSize: 15, color: C.muted, lineHeight: 1.7, marginBottom: 24, fontWeight: 600 }}>
        Bienvenido a FlashBox, <strong style={{ color: C.text }}>{data.nombre} {data.apellido}</strong>.
      </p>
      <div style={{ background: "#fffbeb", border: "1px solid #fde68a", borderRadius: 12, padding: "14px 18px", marginBottom: 12, fontSize: 13, color: "#92400e", fontWeight: 600, lineHeight: 1.7, textAlign: "left" }}>
        📧 Revisa tu correo <strong>{data.email}</strong> y confírmalo para poder iniciar sesión.
      </div>
      <div style={{ background: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: 12, padding: "14px 18px", marginBottom: 24, fontSize: 13, color: "#166534", fontWeight: 600, lineHeight: 1.7, textAlign: "left" }}>
        📱 Una vez verificada tu cuenta, te notificaremos por WhatsApp al <strong>{data.telefono_pais || "+503"} {data.telefono}</strong>.
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        <button onClick={() => window.location.href = "/login"} style={btnStyle(C.blue)}>
          Ir a iniciar sesión →
        </button>
        <button onClick={() => window.location.href = "https://wa.me/50378427302"} style={btnStyle("#25d366")}>
          💬 Contactar vía WhatsApp
        </button>
      </div>
    </div>
  );
}

// ─── HELPERS ─────────────────────────────────────────────────────
function btnStyle(bg, color = "#ffffff", outline = false) {
  return {
    width: "100%", padding: "14px", borderRadius: 12,
    border: outline ? `2px solid ${bg}` : "none",
    background: outline ? "transparent" : bg,
    color: outline ? color : color,
    fontFamily: "inherit", fontSize: 15, fontWeight: 800,
    cursor: "pointer", transition: "opacity .2s",
    letterSpacing: 0.3,
  };
}

// ─── MAIN ─────────────────────────────────────────────────────────
export default function FlashboxAuth() {
  const [mode, setMode]               = useState("login");
  const [step, setStep]               = useState(0);
  const [loading, setLoading]         = useState(false);
  const [resetMode, setResetMode]     = useState(ENLACE_RECUPERACION);
  const [newPassword, setNewPassword] = useState("");
  const [resetMsg, setResetMsg]       = useState("");

  const [loginData, setLoginData]   = useState({ email: "", password: "" });
  const [loginError, setLoginError] = useState(ENLACE_EXPIRADO ? "El enlace para cambiar tu contraseña expiró o ya fue usado. Solicita uno nuevo con \"¿Olvidaste tu contraseña?\"." : "");

  const [regData, setRegData] = useState({
    email: "", password: "", confirm: "",
    nombre: "", apellido: "", telefono: "", telefono_pais: "+503",
    dui: null, dui_numero: "",
  });

  useEffect(() => {
  // Leer sesión actual al cargar — detecta si viene de link de recuperación
  supabase.auth.getSession().then(({ data: { session } }) => {
    if (session?.user) {
      // Revisar si hay token de recovery en la URL
      const hash = window.location.hash;
      if (hash.includes("type=recovery") || hash.includes("access_token")) {
        setResetMode(true);
      }
    }
  });

  // También escuchar en tiempo real
  const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
    if (event === "PASSWORD_RECOVERY") setResetMode(true);
  });

  return () => subscription.unsubscribe();
}, []);

  async function handleLogin() {
    if (!loginData.email || !loginData.password) { setLoginError("Completa todos los campos"); return; }
    setLoading(true);
    const { error } = await supabase.auth.signInWithPassword({
      email: loginData.email,
      password: loginData.password,
    });
    setLoading(false);
    if (error) {
      if (error.message.toLowerCase().includes("email not confirmed")) {
        setLoginError("Debes confirmar tu correo antes de continuar. Revisa tu bandeja de entrada.");
      } else {
        setLoginError("Correo o contraseña incorrectos");
      }
    } else {
      window.location.href = "/portal";
    }
  }

  // ── REGISTRO: recibe el archivo directamente desde Step3 ──────
  async function handleRegister(duiFile) {
    setLoading(true);
    const telLimpio = String(regData.telefono).replace(/^\+\d+\s?/, "").trim();
    const telefonoCompleto = (regData.telefono_pais || "+503") + " " + telLimpio;

    // Todo el alta (cuenta con confirmación de correo, DUI y perfil) se hace en un servicio seguro
    const fd = new FormData();
    fd.append("email", regData.email.trim());
    fd.append("password", regData.password);
    fd.append("nombre", regData.nombre);
    fd.append("apellido", regData.apellido);
    fd.append("telefono", telefonoCompleto);
    fd.append("dui_numero", (regData.dui_numero || "").trim());
    fd.append("dui", duiFile);
    try {
      const r = await fetch(REGISTRO_FN, { method:"POST", body: fd });
      const d = await r.json().catch(() => ({}));
      setLoading(false);
      if (!r.ok) {
        const msgs = {
          email_registrado: "Este correo ya está registrado. Inicia sesión.",
          correo_invalido: "El correo no es válido.",
          password_invalida: "La contraseña debe tener al menos 8 caracteres.",
          dui_invalido: "El número de DUI no tiene el formato correcto (00000000-0).",
          dui_archivo_invalido: "La foto del DUI debe ser JPG, PNG o PDF.",
          dui_muy_grande: "La foto del DUI no debe superar 5MB.",
          telefono_invalido: "El teléfono no es válido.",
        };
        alert(msgs[d.error] || "No pudimos crear tu cuenta. Intenta de nuevo en unos minutos.");
        return;
      }
      setStep(3);
    } catch {
      setLoading(false);
      alert("Sin conexión. Intenta de nuevo.");
    }
  }

  const titles    = ["Crea tu cuenta", "Tus datos", "Tu documento", "¡Bienvenido!"];
  const subtitles = [
    "Ingresa tu correo y una contraseña segura",
    "¿Cómo te llamamos?",
    "Necesitamos verificar tu identidad",
    "",
  ];

  // ── PANTALLA NUEVA CONTRASEÑA ─────────────────────────────────
  if (resetMode) return (
    <div style={{ minHeight:"100vh", background:C.bg, fontFamily:"'Nunito Sans','Helvetica Neue',Arial,sans-serif", display:"flex", alignItems:"center", justifyContent:"center", padding:20 }}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Nunito+Sans:wght@400;600;700;800;900&display=swap'); *{box-sizing:border-box;margin:0;padding:0;} input,button{font-family:inherit;}`}</style>
      <div style={{ width:"100%", maxWidth:480 }}>
        <div style={{ textAlign:"center", marginBottom:32 }}>
          <a href="/"><img src={LOGO} alt="FlashBox" style={{ height:52, width:"auto", cursor:"pointer" }} /></a>
        </div>
        <div style={{ background:C.card, borderRadius:24, padding:"36px 32px", boxShadow:"0 8px 40px rgba(27,87,206,0.1)", border:`1px solid ${C.border}` }}>
          <h1 style={{ fontSize:22, fontWeight:900, color:C.text, marginBottom:8 }}>Nueva contraseña 🔐</h1>
          <p style={{ fontSize:14, color:C.muted, marginBottom:24, fontWeight:600 }}>Ingresa tu nueva contraseña para continuar</p>
          <Input label="Nueva contraseña" type="password" placeholder="Mínimo 8 caracteres"
            value={newPassword} onChange={e => setNewPassword(e.target.value)} required />
          {resetMsg && (
            <div style={{ background: resetMsg.includes("Error") ? "#fef2f2" : "#f0fdf4", border:`1px solid ${resetMsg.includes("Error") ? "#fecaca" : "#bbf7d0"}`, borderRadius:10, padding:"10px 14px", fontSize:13, color: resetMsg.includes("Error") ? C.error : C.green, fontWeight:700, marginBottom:16 }}>
              {resetMsg}
            </div>
          )}
          <button onClick={async () => {
            if (newPassword.length < 8) { setResetMsg("Error: Mínimo 8 caracteres"); return; }
            setLoading(true);
            const { error } = await supabase.auth.updateUser({ password: newPassword });
            setLoading(false);
            if (error) { setResetMsg("Error: No se pudo actualizar. Intenta de nuevo."); }
            else { setResetMsg("✓ Contraseña actualizada correctamente. Redirigiendo..."); setTimeout(() => window.location.href = "/portal", 2000); }
          }} disabled={loading} style={{ ...btnStyle(C.blue), opacity: loading ? 0.7 : 1 }}>
            {loading ? "Guardando..." : "Guardar nueva contraseña"}
          </button>
        </div>
        <p style={{ textAlign:"center", fontSize:12, color:C.muted, marginTop:20, fontWeight:600 }}>
          © 2026 FlashBox · Miami → El Salvador · flashbox.com.sv
        </p>
      </div>
    </div>
  );

  return (
    <div style={{ minHeight: "100vh", background: C.bg, fontFamily: "'Nunito Sans','Helvetica Neue',Arial,sans-serif", display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Nunito+Sans:wght@400;600;700;800;900&display=swap');
        * { box-sizing: border-box; margin: 0; padding: 0; }
        input, button { font-family: inherit; }
        input::placeholder { color: #bbb; }
        @keyframes slideUp { from { opacity:0; transform:translateY(20px); } to { opacity:1; transform:translateY(0); } }
      `}</style>

      <div style={{ width: "100%", maxWidth: 480, animation: "slideUp .5s ease forwards" }}>

        <div style={{ textAlign: "center", marginBottom: 32 }}>
          <a href="/"><img src={LOGO} alt="FlashBox" style={{ height: 52, width: "auto", cursor: "pointer" }} /></a>
        </div>

        <div style={{ background: C.card, borderRadius: 24, padding: "36px 32px", boxShadow: "0 8px 40px rgba(27,87,206,0.1)", border: `1px solid ${C.border}` }}>

          {/* ── LOGIN ── */}
          {mode === "login" && (
            <div>
              <h1 style={{ fontSize: 26, fontWeight: 900, color: C.text, marginBottom: 6 }}>Bienvenido de vuelta</h1>
              <p style={{ fontSize: 14, color: C.muted, marginBottom: 28, fontWeight: 600 }}>Ingresa a tu cuenta FlashBox</p>

              <Input label="Correo electrónico" type="email" placeholder="tu@correo.com"
                value={loginData.email} onChange={e => { setLoginData({ ...loginData, email: e.target.value }); setLoginError(""); }} required />
              <Input label="Contraseña" type="password" placeholder="Tu contraseña"
                value={loginData.password} onChange={e => { setLoginData({ ...loginData, password: e.target.value }); setLoginError(""); }}
                onKeyDown={e => { if (e.key === "Enter") handleLogin(); }} required />

              {loginError && (
                <div style={{ background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 10, padding: "10px 14px", fontSize: 13, color: C.error, fontWeight: 700, marginBottom: 16 }}>
                  ⚠ {loginError}
                </div>
              )}

              <button onClick={handleLogin} disabled={loading} style={{ ...btnStyle(C.blue), marginBottom: 14, opacity: loading ? 0.7 : 1 }}>
                {loading ? "Ingresando..." : "Ingresar →"}
              </button>

              <div style={{ textAlign: "center", marginBottom: 20 }}>
                <button onClick={async () => {
                  if (!loginData.email) { setLoginError("Ingresa tu correo primero"); return; }
                  const { error } = await supabase.auth.resetPasswordForEmail(loginData.email, {
                    redirectTo: "https://flashbox.com.sv/login"
                  });
                  if (error) {
                    const m = (error.message || "").toLowerCase();
                    const limite = error.status === 429 || m.includes("rate limit") || m.includes("security purposes") || m.includes("too many");
                    setLoginError(limite ? "Ya enviamos un correo hace un momento. Espera 1 minuto e inténtalo de nuevo (revisa también tu carpeta de spam)." : "No pudimos enviar el correo. Intenta de nuevo en unos minutos.");
                  }
                  else { setLoginError(""); alert("✓ Revisa tu correo para restablecer tu contraseña"); }
                }} style={{ background:"none", border:"none", padding:0, fontSize: 13, color: C.blue, fontWeight: 700, textDecoration: "none", cursor:"pointer" }}>
                  ¿Olvidaste tu contraseña?
                </button>
              </div>

              <div style={{ borderTop: `1px solid ${C.border}`, paddingTop: 24, textAlign: "center" }}>
                <span style={{ fontSize: 14, color: C.muted, fontWeight: 600 }}>¿No tienes cuenta aún? </span>
                <button onClick={() => { setMode("register"); setStep(0); }}
                  style={{ background: "none", border: "none", color: C.blue, fontSize: 14, fontWeight: 800, cursor: "pointer" }}>
                  Regístrate gratis
                </button>
              </div>
            </div>
          )}

          {/* ── REGISTER ── */}
          {mode === "register" && (
            <div>
              {step < 3 && (
                <>
                  <Stepper step={step} />
                  <h1 style={{ fontSize: 22, fontWeight: 900, color: C.text, marginBottom: 4 }}>{titles[step]}</h1>
                  <p style={{ fontSize: 14, color: C.muted, marginBottom: 24, fontWeight: 600 }}>{subtitles[step]}</p>
                </>
              )}

              {step === 0 && <Step1 data={regData} setData={setRegData} onNext={() => setStep(1)} />}
              {step === 1 && <Step2 data={regData} setData={setRegData} onNext={() => setStep(2)} onBack={() => setStep(0)} />}
              {step === 2 && <Step3 data={regData} setData={setRegData} onSubmit={handleRegister} onBack={() => setStep(1)} loading={loading} />}
              {step === 3 && <Step4 data={regData} />}

              {step < 3 && (
                <div style={{ borderTop: `1px solid ${C.border}`, paddingTop: 20, marginTop: 8, textAlign: "center" }}>
                  <span style={{ fontSize: 13, color: C.muted, fontWeight: 600 }}>¿Ya tienes cuenta? </span>
                  <button onClick={() => setMode("login")}
                    style={{ background: "none", border: "none", color: C.blue, fontSize: 13, fontWeight: 800, cursor: "pointer" }}>
                    Ingresar
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        <p style={{ textAlign: "center", fontSize: 12, color: C.muted, marginTop: 20, fontWeight: 600 }}>
          © 2026 FlashBox · Miami → El Salvador · flashbox.com.sv
        </p>
      </div>
    </div>
  );
}
